import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FileSpreadsheet, Search, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, eur, fdate, TONES, CATEGORIES, lineTotals } from "@/lib/api";

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4 rise">
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-[#16163F] sm:text-3xl" data-testid="page-title">{title}</h1>
      <div className="mt-2 h-0.5 w-10 bg-[#A5894E]" />
      {subtitle && <p className="mt-3 text-sm text-[#6B6B80]">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Badge = ({ meta, testid }) =>
  meta ? (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold ${TONES[meta[1]]}`}>{meta[0]}</span>
  ) : null;

export const KpiCard = ({ icon: Icon, label, value, hint, testid, onClick, delay = 0 }) => (
  <button type="button" onClick={onClick} data-testid={testid} style={{ animationDelay: `${delay}ms` }}
    className="card rise group flex w-full items-start gap-4 border-l-4 border-l-[#A5894E] p-5 text-left transition-transform hover:-translate-y-0.5">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#A5894E] text-[#16163F]"><Icon size={20} /></span>
    <span className="min-w-0">
      <span className="block text-xs font-semibold uppercase tracking-wide text-[#6B6B80]">{label}</span>
      <span className="num mt-1 block text-2xl font-extrabold text-[#16163F] lg:text-3xl">{value}</span>
      {hint && <span className="mt-1 block text-xs text-[#6B6B80]">{hint}</span>}
    </span>
  </button>
);

export const Field = ({ label, error, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="lbl">{label}</span>
    {children}
    {error && <span className="mt-1 block text-xs font-medium text-[#B03A3A]">{error}</span>}
  </label>
);

export const Empty = ({ text = "Aucun élément à afficher." }) => <div className="p-10 text-center text-sm text-[#6B6B80]">{text}</div>;
export const Spinner = () => <div className="flex justify-center p-12"><Loader2 className="animate-spin text-[#A5894E]" /></div>;

export function ExcelButton({ title, columns, rows, testid = "export-excel-btn" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const cols = columns.filter((c) => c.export !== false).map((c) => ({ key: c.key, label: c.label, type: c.type || "" }));
      const data = rows.map((r) => Object.fromEntries(columns.filter((c) => c.export !== false).map((c) => [c.key, c.value ? c.value(r) : r[c.key]])));
      await download("/export/list", { title, columns: cols, rows: data });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <button type="button" className="btn-secondary" onClick={go} disabled={busy} data-testid={testid}>
      <FileSpreadsheet size={16} /> Exporter en Excel
    </button>
  );
}

const cellValue = (c, r) => (c.value ? c.value(r) : r[c.key]);
const fmt = (c, v) => (c.type === "money" ? eur(v) : c.type === "date" ? fdate(v) : v ?? "");

export function DataTable({ columns, rows, title, onRowClick, filters, pageSize = 15, testid = "data-table", searchPlaceholder = "Rechercher…", initialSort }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = s ? rows.filter((r) => columns.some((c) => String(cellValue(c, r) ?? "").toLowerCase().includes(s))) : rows;
    if (sort) {
      const c = columns.find((x) => x.key === sort.key);
      out = [...out].sort((a, b) => {
        const va = cellValue(c, a), vb = cellValue(c, b);
        const r = typeof va === "number" && typeof vb === "number" ? va - vb : String(va ?? "").localeCompare(String(vb ?? ""), "fr", { numeric: true });
        return sort.dir === "asc" ? r : -r;
      });
    }
    return out;
  }, [rows, q, sort, columns]);
  useEffect(() => setPage(0), [q, rows]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const view = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const toggle = (k) => setSort((s) => (s?.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  return (
    <div className="card overflow-hidden rise" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3 border-b border-[#E4E4EC] p-4">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B80]" />
          <input className="inp pl-9" placeholder={searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
        </div>
        {filters}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[#6B6B80]" data-testid={`${testid}-count`}>{filtered.length} élément(s)</span>
          {title && <ExcelButton title={title} columns={columns} rows={filtered} testid={`${testid}-export`} />}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="dt">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} onClick={() => c.sortable !== false && toggle(c.key)} className={`${c.sortable !== false ? "cursor-pointer select-none" : ""} ${c.type === "money" ? "text-right" : ""}`}>
                  <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={r.id || i} onClick={() => onRowClick?.(r)} className={onRowClick ? "cursor-pointer" : ""} data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className={c.type === "money" ? "num whitespace-nowrap text-right font-semibold" : c.type === "date" ? "num whitespace-nowrap" : ""}>
                    {c.render ? c.render(r) : fmt(c, cellValue(c, r))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!view.length && <Empty />}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-[#E4E4EC] p-3 text-sm">
        <span className="text-xs text-[#6B6B80]">Page {page + 1} / {pages}</span>
        <button className="btn-ghost" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft size={16} /></button>
        <button className="btn-ghost" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

export function Select({ value, onChange, options, placeholder, testid, className = "" }) {
  return (
    <select className={`inp ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function DirectoryPicker({ value, onSelect, testid = "directory-picker", placeholder = "Rechercher dans l'annuaire Microsoft 365…" }) {
  const [q, setQ] = useState(value || "");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef();
  useEffect(() => setQ(value || ""), [value]);
  const search = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.length < 2) return setRes([]);
    timer.current = setTimeout(() => api.get("/directory/search", { params: { q: v } }).then((r) => { setRes(r.data); setOpen(true); }), 250);
  };
  return (
    <div className="relative">
      <input className="inp" value={q} onChange={(e) => search(e.target.value)} placeholder={placeholder} data-testid={testid} onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && res.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[#E4E4EC] bg-white shadow-lg">
          {res.map((p, i) => (
            <button type="button" key={p.email} data-testid={`${testid}-option-${i}`} className="block w-full px-3 py-2 text-left text-sm hover:bg-[#A5894E]/10"
              onMouseDown={() => { onSelect(p); setQ(p.name); setOpen(false); }}>
              <span className="font-semibold">{p.name}</span> {!p.active && <span className="ml-1 rounded bg-[#B03A3A] px-1.5 text-[10px] font-bold text-white">Compte désactivé</span>}
              <span className="block text-xs text-[#6B6B80]">{p.email} · {p.department} · {p.office}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const emptyLine = () => ({ designation: "", reference: "", category: "materiel", quantity: 1, unit_price: 0, vat_rate: 20 });

export function LinesEditor({ lines, onChange, readOnly, testid = "lines" }) {
  const set = (i, k, v) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const t = lineTotals(lines);
  return (
    <div data-testid={testid}>
      <div className="overflow-x-auto rounded-md border border-[#E4E4EC]">
        <table className="dt">
          <thead><tr><th>Désignation</th><th>Réf.</th><th>Catégorie</th><th>Qté</th><th>PU HT</th><th>TVA %</th><th className="text-right">Total HT</th>{!readOnly && <th />}</tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td className="min-w-[200px]"><input className="inp" value={l.designation} disabled={readOnly} onChange={(e) => set(i, "designation", e.target.value)} data-testid={`${testid}-designation-${i}`} /></td>
                <td className="w-28"><input className="inp" value={l.reference || ""} disabled={readOnly} onChange={(e) => set(i, "reference", e.target.value)} /></td>
                <td className="w-36">
                  <select className="inp" value={l.category} disabled={readOnly} onChange={(e) => set(i, "category", e.target.value)} data-testid={`${testid}-category-${i}`}>
                    {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="w-20"><input type="number" min="0" step="any" className="inp num" value={l.quantity} disabled={readOnly} onChange={(e) => set(i, "quantity", e.target.value)} data-testid={`${testid}-qty-${i}`} /></td>
                <td className="w-28"><input type="number" min="0" step="0.01" className="inp num" value={l.unit_price} disabled={readOnly} onChange={(e) => set(i, "unit_price", e.target.value)} data-testid={`${testid}-price-${i}`} /></td>
                <td className="w-20">
                  <select className="inp num" value={l.vat_rate} disabled={readOnly} onChange={(e) => set(i, "vat_rate", e.target.value)}>
                    {[20, 10, 5.5, 2.1, 0].map((v) => <option key={v} value={v}>{String(v).replace(".", ",")}</option>)}
                  </select>
                </td>
                <td className="num whitespace-nowrap text-right font-semibold">{eur((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</td>
                {!readOnly && <td><button type="button" className="btn-ghost" onClick={() => onChange(lines.filter((_, j) => j !== i))} disabled={lines.length === 1} data-testid={`${testid}-remove-${i}`}><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        {!readOnly ? <button type="button" className="btn-ghost" onClick={() => onChange([...lines, emptyLine()])} data-testid={`${testid}-add`}><Plus size={15} /> Ajouter une ligne</button> : <span />}
        <div className="num flex gap-5 text-sm" data-testid={`${testid}-totals`}>
          <span>HT <b>{eur(t.ht)}</b></span><span>TVA <b>{eur(t.vat)}</b></span><span>TTC <b className="text-base">{eur(t.ttc)}</b></span>
        </div>
      </div>
    </div>
  );
}

export const validLines = (lines) => lines.length > 0 && lines.every((l) => l.designation.trim() && Number(l.quantity) > 0 && Number(l.unit_price) >= 0);
export const cleanLines = (lines) => lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price), vat_rate: Number(l.vat_rate) }));

export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/files", fd);
  return r.data;
}
